import React from 'react';
import { COLORS, RADIUS, SPACING, TYPOGRAPHY, SHADOWS } from '../../config/branding';
import Button from './Button';

/**
 * ErrorState Component - Display error messages with retry functionality
 *
 * @param {string} title - Error title
 * @param {string} message - Error message
 * @param {function} onRetry - Retry callback function
 * @param {boolean} showRetry - Show retry button
 * @param {React.ReactNode} icon - Custom error icon
 */
const ErrorState = ({
  title = 'Something went wrong',
  message = 'An error occurred while loading this content. Please try again.',
  onRetry,
  showRetry = true,
  icon = null,
}) => {
  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: SPACING['2xl'],
    textAlign: 'center',
    backgroundColor: COLORS.surface,
    borderRadius: RADIUS.xl,
    border: `1px solid ${COLORS.border}`,
    boxShadow: SHADOWS.sm,
    minHeight: '200px',
  };

  const iconStyle = {
    width: '64px',
    height: '64px',
    marginBottom: SPACING.lg,
    color: COLORS.error,
  };

  const titleStyle = {
    fontSize: TYPOGRAPHY.fontSize['2xl'],
    fontWeight: TYPOGRAPHY.fontWeight.bold,
    color: COLORS.text.primary,
    marginBottom: SPACING.sm,
  };

  const messageStyle = {
    fontSize: TYPOGRAPHY.fontSize.base,
    color: COLORS.text.secondary,
    marginBottom: SPACING.xl,
    maxWidth: '500px',
  };

  const defaultIcon = (
    <svg
      style={iconStyle}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );

  return (
    <div style={containerStyle}>
      {icon || defaultIcon}
      <h3 style={titleStyle}>{title}</h3>
      <p style={messageStyle}>{message}</p>
      {showRetry && onRetry && (
        <Button variant="primary" onClick={onRetry}>
          Try Again
        </Button>
      )}
    </div>
  );
};

/**
 * ErrorBoundary - Catch React errors and display fallback UI
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorState
          title="Application Error"
          message={this.state.error?.message || 'An unexpected error occurred.'}
          onRetry={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * EmptyState - Display when no data is available
 */
export const EmptyState = ({
  title = 'No data found',
  message = 'There is no data to display at this time.',
  icon = null,
  action = null,
}) => {
  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: SPACING['2xl'],
    textAlign: 'center',
    minHeight: '200px',
  };

  const iconStyle = {
    width: '64px',
    height: '64px',
    marginBottom: SPACING.lg,
    color: COLORS.gray[400],
  };

  const titleStyle = {
    fontSize: TYPOGRAPHY.fontSize.xl,
    fontWeight: TYPOGRAPHY.fontWeight.semibold,
    color: COLORS.text.primary,
    marginBottom: SPACING.sm,
  };

  const messageStyle = {
    fontSize: TYPOGRAPHY.fontSize.base,
    color: COLORS.text.muted,
    marginBottom: SPACING.lg,
  };

  const defaultIcon = (
    <svg
      style={iconStyle}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
      />
    </svg>
  );

  return (
    <div style={containerStyle}>
      {icon || defaultIcon}
      <h3 style={titleStyle}>{title}</h3>
      <p style={messageStyle}>{message}</p>
      {action && action}
    </div>
  );
};

export default ErrorState;
